import Lightning from '@lightningjs/sdk/src/Lightning';
import AbstractPlayer from 'components/pages/playback/player-components/AbstractPlayer';
import BrightcovePlayerEngine from 'components/pages/playback/player-engines/BrightcoveEngine';
import { AdCuePoint } from 'components/pages/playback/ad-managers/AbstractAdManager';
import BrightcovePlayerEventConsumer from 'components/pages/playback/player-event-consumers/BrightcovePlayerEventConsumer';
import BrightcoveAdManager from 'components/pages/playback/ad-managers/BrightcoveAdManager';
import { Ad, CustomPlayerEvent } from 'types/player';

interface BrightcovePlayerTemplateSpec
  extends Lightning.Component.TemplateSpec {
  token: string | undefined;
  bcVideoId: string | undefined;
  AdManager: typeof BrightcoveAdManager;
}

type WrapperAdState = {
  podIndex: number;
  wrapperAdSystem: string;
  wrapperAdId: string;
  wrapperCreativeId: string;
};

export default class BrightcovePlayer extends AbstractPlayer<BrightcovePlayerTemplateSpec> {
  readonly playerType = 'brightcove';
  private wrapperAdState: WrapperAdState | null = null;

  protected override _AdManager = this.getByRef('AdManager')!;

  protected override _engine: BrightcovePlayerEngine | undefined;

  token: BrightcovePlayerTemplateSpec['token'];
  bcVideoId: BrightcovePlayerTemplateSpec['bcVideoId'];

  static override _template(): Lightning.Component.Template<BrightcovePlayerTemplateSpec> {
    return {
      ...super._template(),
      AdManager: {
        type: BrightcoveAdManager,
        passSignals: {
          cuePointsChanged: true,
        },
      },
    };
  }

  override initialize(consumer: Lightning.Component): void {
    super.initialize(consumer);

    if (!this.token) {
      this.handleError('token undefined');
      return;
    }
    if (!this.bcVideoId) {
      this.handleError('bcVideoId undefined');
      return;
    }

    this._engine = new BrightcovePlayerEngine();
    this._engine.consumer(consumer as BrightcovePlayerEventConsumer);

    this.loader();
  }

  protected async loader() {
    if (!this._engine) {
      this.handleError('engine undefined');
      return;
    }
    if (!this.token) {
      this.handleError('token undefined');
      return;
    }
    if (!this.bcVideoId) {
      this.handleError('bcVideoId undefined');
      return;
    }

    this.updateVideoElemDimensions({
      x: this._absoluteX,
      y: this._absoluteY,
      w: this.w,
      h: this.h,
    });

    /*
      For Brightcove, we don't need to pass in startTime to load().
      We seek to startTime after onContentLoaded(), since we initialize videoJS instance there,
      which is required for streamTimeForContentTime()
    */
    await this._engine.load(
      undefined,
      this.token,
      this.bcVideoId,
      this.videoId,
    );
    await this._AdManager.onContentLoaded();

    if (this.startTime !== undefined) {
      this.rawSeek(
        this.startTime === 0
          ? 0
          : this.streamTimeForContentTime(this.startTime)!,
      );
    }

    if (this._onStreamAdaption) {
      this._engine.onAdaption(this._onStreamAdaption);
    }

    if (this._onStreamLoaded) {
      const srcUrl = this._engine.getSrcUrl();
      if (srcUrl) this._onStreamLoaded(srcUrl);
    }

    if (this._onPlaybackError) {
      this._engine.onError(this._onPlaybackError);
    }
  }

  override open(): void {
    super.open();
  }

  override close(): void {
    super.close();
    this.token = undefined;
    this.bcVideoId = undefined;
    this.wrapperAdState = null;
    this._engine?.destroy();
  }

  override isPlaying(): boolean {
    return this._engine?.isPlaying() ?? false;
  }

  override resume(): void {
    if (!this.isPlaying()) {
      this._engine?.play();
    }
  }

  override pause(): void {
    this._engine?.pause();
  }

  override seek(seekTime: number) {
    super.seek(seekTime);
    this.firePlayerEvent(CustomPlayerEvent.SEEKING, undefined, seekTime);
  }

  override currentRawTime(): number {
    return this._engine?.getCurrentTime() ?? 0;
  }

  override currentContentTime(): number {
    const currentRawTime = this._engine?.getCurrentTime() ?? 0;
    const contentTime = this.contentTimeForStreamTime(currentRawTime);
    return contentTime ?? currentRawTime;
  }

  protected override createManager(videoEl: HTMLVideoElement): void {
    console.error('createManager not implemented.');
  }

  protected override rawSeek(seekTime: number): void {
    this._engine?.seek(seekTime);
  }

  protected override updateVideoElemDimensions(dimensions: {
    x?: number;
    y?: number;
    w?: number;
    h?: number;
  }): void {
    this._engine?.updateDimensions(dimensions);
  }

  override streamTimeForContentTime(streamTime: number): number | undefined {
    return this._AdManager.getContentTime(streamTime);
  }

  override contentTimeForStreamTime(contentTime: number): number | undefined {
    return this._AdManager.getStreamTime(contentTime);
  }

  override previousCuePointForStreamTime(time: number): AdCuePoint | undefined {
    console.error('previousCuePointForStreamTime not implemented.');
    return undefined;
  }

  override extractAd(event: any): Ad | undefined {
    const { ads } = event.target.player;
    const { creative_id, duration, id, index, system, title } = ads.ad;
    const { index: podIndex } = ads.pod;

    if (!this.wrapperAdState || this.wrapperAdState.podIndex !== podIndex) {
      // we assume the first ad we encounter in a new pod is the first ad in the pod
      this.wrapperAdState = {
        podIndex,
        wrapperAdSystem: system,
        wrapperAdId: id,
        wrapperCreativeId: creative_id,
      };
    }

    const ad: Ad = {
      getAdId: () => id,
      getAdSystem: () => system,
      getApiFramework: () => null, // we don't use this value
      getCreativeId: () => creative_id,
      getDuration: () => duration,
      getTitle: () => title,
      getWrapperAdIds: () => [this.wrapperAdState!.wrapperAdId],
      getWrapperAdSystems: () => [this.wrapperAdState!.wrapperAdSystem],
      getWrapperCreativeIds: () => [this.wrapperAdState!.wrapperCreativeId],
      adStitcher: () => 'brightcoveSSAI',
      getAdPodInfo: () => ({
        getPodIndex: () => podIndex,
        getAdPosition: () => index,
      }),
    };

    return ad;
  }
}
